import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
    Box,
    List,
    ListItem,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import makeStyles from '@mui/styles/makeStyles'
import { Close } from 'genesis-suite/icons'
import { userService } from '../../../lib/services'
import { CloudContext } from '../../contexts/CloudContext'
import { DimLoader, SwalContext } from 'genesis-suite/components'
import { useSnackbar } from 'notistack'

const useStyles = makeStyles(({ palette, spacing }) => ({
    dialog: {
        minHeight: '40vh',
        maxWidth: 900,
        minWidth: 900,
    },
    dialogContent: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    emailBox: {
        padding: spacing(2),
        borderBottom: `1px solid ${palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
    },
    closeButton: {
        position: 'absolute',
        top: spacing(1),
        right: spacing(1),
    },
    gridItem: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
    },
    filterItem: {
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#f9f9f9',
        marginBottom: spacing(1),
        borderRadius: spacing(0.5),
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: spacing(1, 2),
    },
    selectedFiltersBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: '#f9f9f9',
        zIndex: 1,
        padding: spacing(1),
        borderBottom: `1px solid ${palette.divider}`,
    },
    selectedFiltersList: {
        maxHeight: '300px',
        overflowY: 'auto',
        padding: spacing(1),
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#fff',
        marginBottom: spacing(1),
        borderRadius: spacing(0.5),
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: spacing(1, 2),
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
    },
}))

const NetworkFiltersPermissionModal = ({ open, onClose, userTableRef, selectedUserList }) => {
    const classes = useStyles({})
    const [availableFilters, setAvailableFilters] = useState([])
    const [selectedFilters, setSelectedFilters] = useState([])
    const { selectedCloud: context } = useContext(CloudContext)
    const { alert, confirm } = useContext(SwalContext)

    const [loader, setLoader] = useState({
        saveLoading: false,
        contentLoad: true,
    })

    const { enqueueSnackbar: showSnackbar } = useSnackbar()

    const fetchFilters = useCallback(async () => {
        try {
            setLoader({ saveLoading: false, contentLoad: true })
            const users = userTableRef?.current?.getUsers()
            if (!users) {
                alert('There were no users selected to network!', { type: 'error' })
                setLoader({ saveLoading: false, contentLoad: false })
                return
            }
            const [availableResponse, selectedResponse] = await Promise.all([
                userService.getNetworkfilters(context, -1),
                userService.getUserNetworkfilters(context, selectedUserList[0]),
            ])
            if (JSON.stringify(availableFilters) !== JSON.stringify(availableResponse)) {
                const filteredAvailableFilters = availableResponse?.filter(
                    availableFilter =>
                        !selectedResponse?.some(selectedFilter => availableFilter.id === selectedFilter.id)
                )

                setAvailableFilters(filteredAvailableFilters ?? [])
            }
            if (JSON.stringify(selectedFilters) !== JSON.stringify(selectedResponse)) {
                setSelectedFilters(selectedResponse ?? [])
            }
        } catch (error) {
            console.error('Error fetching filters:', error)
        } finally {
            setLoader({ saveLoading: false, contentLoad: false })
        }
    }, [availableFilters, selectedFilters])

    useEffect(() => {
        fetchFilters()
    }, [])

    const addFilter = filter => {
        if (!selectedFilters.some(selected => selected.id === filter.id)) {
            setSelectedFilters(prevSelected => [filter, ...prevSelected])
            setAvailableFilters(prevAvailable => prevAvailable.filter(item => item.id !== filter.id))
        }
    }

    const removeFilter = id => {
        const filterToRemove = selectedFilters.find(item => item.id === id)
        if (filterToRemove) {
            setAvailableFilters(prevAvailable => [filterToRemove, ...prevAvailable])
            setSelectedFilters(prevSelected => prevSelected.filter(item => item.id !== id))
        }
    }

    const removeAllFilters = async () => {
        const response = await confirm(
            `Are you sure to delete all network filters permissions for the user ${
                selectedUserList && selectedUserList[0]
            }`,
            {
                type: 'warning',
                confirmButtonProps: { text: 'Ok' },
            }
        )
        if (response.dismiss) return

        setAvailableFilters(prevAvailable => [...prevAvailable, ...selectedFilters])
        setSelectedFilters([])
        const users = userTableRef?.current?.getUsers()
        if (!users) {
            alert('There were no users selected to delete all!', { type: 'error' })
            return
        }
        try {
            setLoader({ saveLoading: true, contentLoad: false })
            await doSaveUserFilterPermissions(true)
            showSnackbar('Delete successful', { variant: 'success' })
        } catch (e) {
            console.error(e)
            showSnackbar('Failed to delete', { variant: 'error' })
        } finally {
            setLoader({ saveLoading: false, contentLoad: false })
        }
    }

    const doSaveUserFilterPermissions = async (isDeleteAll = false, filtersPermissions = []) => {
        await userService.saveUserFilterPermissions(
            {
                UserFilterPermissionsIds: filtersPermissions,
                ModelName: context,
                UserId: selectedUserList[0],
                DeleteAll: isDeleteAll,
            },
            isDeleteAll ? 'delete' : 'post'
        )
    }
    const handleSaveNetworkPermission = async () => {
        try {
            setLoader({ saveLoading: true, contentLoad: false })

            const filtersPermissions = selectedFilters?.reduce((acc, f) => {
                acc[f.id] = 1
                return acc
            }, {})

            await doSaveUserFilterPermissions(false, filtersPermissions)
            showSnackbar('Save successful', { variant: 'success' })
        } catch (e) {
            console.error(e)
            showSnackbar('Failed to save', { variant: 'error' })
        } finally {
            setLoader({ saveLoading: false, contentLoad: false })
        }
    }
    return (
        <React.Fragment>
            {loader.saveLoading && <DimLoader />}
            <Dialog
                open={open}
                classes={{ paper: classes.dialog }}
                onClose={() => onClose(false)}
                aria-labelledby="network-filters-title"
            >
                <DialogTitle id="network-filters-title">Network Filters</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <IconButton classes={{ root: classes.closeButton }} onClick={() => onClose(false)} size="large">
                        <Close />
                    </IconButton>
                    <Grid container spacing={0}>
                        <Grid
                            item
                            xs={3}
                            className={` ${classes.gridItem}`}
                            style={{ padding: '16px 16px', minWidth: '200px' }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: '#f5f5f5',
                                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                }}
                            >
                                <Typography
                                    noWrap
                                    style={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        display: 'block',
                                    }}
                                >
                                    {selectedUserList && selectedUserList[0]}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={4}
                            className={classes.gridItem}
                            style={{ borderLeft: '1px solid #ccc', padding: '10px' }}
                        >
                            {loader.contentLoad ? (
                                <div className={classes.loaderContainer}>
                                    <CircularProgress />
                                </div>
                            ) : availableFilters?.length === 0 || !availableFilters ? (
                                <Typography className={classes.loaderContainer}>
                                    No available network filters found.
                                </Typography>
                            ) : (
                                <List style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {availableFilters?.map(item => (
                                        <ListItem key={item?.id} className={classes.filterItem}>
                                            <Typography>{item?.name}</Typography>
                                            <IconButton size="small" onClick={() => addFilter(item)}>
                                                <AddIcon />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Grid>

                        <Grid
                            item
                            xs={5}
                            className={classes.gridItem}
                            style={{
                                borderLeft: '1px solid #ccc',
                                padding: '10px',
                                position: 'relative',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                            }}
                        >
                            <div className={classes.selectedFiltersBox}>
                                <Typography variant="h6" style={{ fontWeight: 'bold', margin: 0 }}>
                                    Selected Network Filters
                                </Typography>
                                <Button
                                    disabled={selectedFilters?.length === 0}
                                    variant="text"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={removeAllFilters}
                                    sx={{
                                        fontWeight: 'bold',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        marginTop: 0,
                                        backgroundColor:
                                            selectedFilters?.length === 0
                                                ? 'rgba(0, 0, 0, 0.12)'
                                                : 'rgba(255, 0, 0, 0.1)',
                                        color: selectedFilters?.length === 0 ? 'rgba(0, 0, 0, 0.26)' : 'red',
                                        '&.Mui-disabled': {
                                            color: 'rgba(0, 0, 0, 0.26)',
                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        },
                                    }}
                                >
                                    Delete All
                                </Button>
                            </div>
                            {loader.contentLoad ? (
                                <div className={classes.loaderContainer}>
                                    <CircularProgress />
                                </div>
                            ) : selectedFilters?.length === 0 || !selectedFilters ? (
                                <Typography className={classes.loaderContainer}>
                                    No selected network filters found.
                                </Typography>
                            ) : (
                                <List className={classes.selectedFiltersList}>
                                    {selectedFilters?.map(item => (
                                        <ListItem key={item?.id} className={classes.listItem}>
                                            <Typography>{item?.name}</Typography>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeFilter(item?.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Grid>
                    </Grid>
                    <Box padding="16px" borderTop="1px solid #ccc" textAlign="right">
                        <Button variant="contained" color="primary" onClick={handleSaveNetworkPermission}>
                            Save Filter Permissions
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    )
}

export default NetworkFiltersPermissionModal
